import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Column } from 'primereact/column';
import { DataTable, type DataTableValue } from 'primereact/datatable';
import { MdDownload } from 'react-icons/md';
import {
	buildPaginatorTemplate,
	PAGINATOR_CURRENT_REPORT,
	paginatorPassThrough,
} from '@/components/table/pagination/Paginator';
import styles from './statement-table.module.css';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { GenericResponse, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';

interface TopUpStatementDto {
	Period: string;
	Balance: number;
	PaymentCount: number;
	PeriodDate: string;
}

interface StatementData {
	period: string;
	balance: string;
	periodDate?: string;
}

interface TableProps {
	columns: {
		field: 'period' | 'balance' | 'action';
		header: string;
	}[];
}

const StatementTable = ({ columns }: TableProps) => {
	const [options, setOptions] = useState({ skip: 0, take: 5 });

	const { data: statementData = [], isLoading } = useQuery({
		queryKey: ['Billing.TopUpStatements'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<TopUpStatementDto>>>(
				ENDPOINTS['/api/payments/GetTopUpStatements'],
				{}
			);

			// Transform to table format
			return result.data.Data.Items.map(item => ({
				period: item.Period,
				balance: `₱${item.Balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
				periodDate: item.PeriodDate
			}));
		},
		initialData: []
	});

	const handleDownloadPdf = async (rowData: StatementData) => {
		try {
			const response = await axiosInstance.post(
				ENDPOINTS['/api/payments/ExportAccountStatementPdf'],
				{ Period: rowData.period },
				{ responseType: 'blob' }
			);
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `statement-${rowData.period.replace(' ', '-')}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(url);
			showNotification('PDF downloaded successfully');
		} catch (error) {
			showNotification('Failed to download PDF');
		}
	};

	const handleDownloadCsv = async (rowData: StatementData) => {
		try {
			const response = await axiosInstance.post(
				ENDPOINTS['/api/payments/ExportAccountStatementCsv'],
				{ Period: rowData.period },
				{ responseType: 'blob' }
			);
			const blob = new Blob([response.data], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `statement-${rowData.period.replace(' ', '-')}.csv`);
			document.body.appendChild(link);
			link.click();
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(url);
			showNotification('CSV downloaded successfully');
		} catch (error) {
			showNotification('Failed to download CSV');
		}
	};

	return (
		<DataTable
			value={statementData}
			totalRecords={statementData.length}
			paginator
			rowsPerPageOptions={[5, 10, 25, 50]}
			first={options.skip}
			rows={options.take}
			onPage={(event) => {
				setOptions({
					...options,
					skip: event.first,
					take: event.rows,
				});
			}}
			className={styles.customDatatable}
			tableStyle={{ minWidth: '50rem', width: '100%' }}
			pt={{ paginator: paginatorPassThrough }}
			paginatorTemplate={buildPaginatorTemplate()}
			currentPageReportTemplate={PAGINATOR_CURRENT_REPORT}
			loading={isLoading}
		>
			{columns.map((col) => (
				<Column
					key={String(col.field)}
					field={
						col.field === 'action'
							? undefined
							: (col.field as string)
					}
					header={col.header}
					sortable={false}
					body={(rowData: StatementData) => {
						if (col.field === 'action') {
							return (
								<div className="flex gap-3 my-2">
									<RoundedFilledButton
										id="download pdf"
										className="with-icon-button"
										icon={MdDownload}
										label="PDF"
										onClick={() => handleDownloadPdf(rowData)}
										width="110px"
										height="32px"
									/>
									<RoundedFilledButton
										id="download csv"
										className="with-icon-button"
										icon={MdDownload}
										label="CSV"
										onClick={() => handleDownloadCsv(rowData)}
										width="110px"
										height="32px"
									/>
								</div>
							);
						}

						const cellValue = rowData[col.field as keyof StatementData];
						return cellValue;
					}}
				/>
			))}
		</DataTable>
	);
};

export default StatementTable;
